import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { getClassName } from '../../../utils/ui/classes';
import Tooltip from '../Tooltip';
import Badge from '../Badge';
import ArrowUpIcon from '../icons/ArrowUpIcon';
import ArrowDownIcon from '../icons/ArrowDownIcon';
import LoadingCircle from '../../ui/LoadingCircle';
import Alert from '../../ui/Alert';
import StarIcon from '../icons/StarIcon';

const baseClassName =
  'grid grid-cols-1 overflow-hidden grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6';
export const StatsContainer = ({ children, className }) => (
  <dl className={getClassName(baseClassName, className)}>{children}</dl>
);

StatsContainer.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
};

export const Card = ({ id, label, info, children, className, onClick }) => (
  <div
    className={getClassName(
      'px-4 py-5 bg-white rounded-lg shadow sm:p-6',
      className,
    )}
    onClick={onClick}
  >
    <div className="flex items-center space-x-2">
      <dt className="text-base font-normal text-gray-900">{label}</dt>
      {info && <Tooltip id={id}>{info}</Tooltip>}
    </div>
    <div className="flex items-baseline justify-between mt-1">{children}</div>
  </div>
);

export const ValueItem = ({ valueText, helptext }) => (
  <div className="flex items-baseline text-2xl font-semibold text-gray-800">
    {valueText}
    {helptext && (
      <span className="ml-2 text-sm font-medium text-gray-500">{helptext}</span>
    )}
  </div>
);

const iconClasses = 'flex-shrink-0 self-center h-5 w-5 -ml-1 mr-0.5';
function getTrendIconClass(isTrendingUp) {
  return getClassName(iconClasses, {
    'text-green-500': isTrendingUp,
    'text-red-500': !isTrendingUp,
  });
}

export const TrendItem = ({ delta }) => {
  const isTrendingUp = delta >= 0;
  const className = getTrendIconClass(isTrendingUp);
  const screenReaderText = isTrendingUp ? 'Increased by' : 'Decreased by';
  return (
    <Badge color={isTrendingUp ? 'green' : 'red'}>
      {isTrendingUp && <ArrowUpIcon className={className} />}
      {!isTrendingUp && <ArrowDownIcon className={className} />}
      <div className="sr-only">{screenReaderText}</div>
      {delta}%
    </Badge>
  );
};

function getStarColorClass(rating) {
  if (rating <= 2) {
    return 'text-red-500';
  }

  if (rating < 4) {
    return 'text-yellow-500';
  }

  return 'text-green';
}
export const StarRating = ({ value, maxRating }) => {
  const rating = Math.round(value * 2) / 2;
  const ceil = Math.ceil(rating);

  const stars = new Array(maxRating).fill('empty').map((_, index) => {
    const currentValue = index + 1;
    if (currentValue < ceil) {
      return 'filled';
    }

    if (currentValue > ceil) {
      return 'none';
    }

    const diff = ceil - rating;
    if (diff === 0) {
      return 'filled';
    }

    if (diff === 0.5) {
      return 'half';
    }

    return 'none';
  });

  return (
    <div className="flex">
      {stars.map((fillType, i) => (
        <StarIcon
          key={i}
          fillType={fillType}
          className={getClassName('w-5 h-5', getStarColorClass(rating))}
        />
      ))}
    </div>
  );
};

StarRating.propTypes = {
  value: PropTypes.number.isRequired,
  maxRating: PropTypes.number,
};

StarRating.defaultProps = {
  maxRating: 5,
};

export const RatingCard = ({
  id,
  label,
  loaded,
  error,
  data,
  info,
  maxRating,
  className,
}) => {
  let body;
  const { t } = useTranslation();

  if (error) {
    body = <Alert type="error" title={t('PROBLEM_LOADING_DATA')} />;
  } else if (!loaded) {
    body = <LoadingCircle text={t('LOADING')} />;
  } else {
    body = <StarRating value={data.value} maxRating={maxRating} />;
  }
  return (
    <Card id={id} label={label} info={info} className={className}>
      {body}
    </Card>
  );
};

export const PercentCard = ({
  id,
  label,
  loaded,
  error,
  data,
  info,
  className,
}) => {
  const valueText = data.value >= 0 && `${data.value.toFixed(2)}%`;
  const { t } = useTranslation();
  let body;
  if (error) {
    body = <Alert type="error" title={t('PROBLEM_LOADING_DATA')} />;
  } else if (!loaded) {
    body = <LoadingCircle text={t('LOADING')} />;
  } else {
    body = [
      <ValueItem key={0} valueText={valueText} />,
      typeof data.delta !== 'undefined' ? (
        <TrendItem key={1} delta={data.delta.toFixed(2)} />
      ) : null,
    ];
  }

  return (
    <Card id={id} label={label} info={info} className={className}>
      {body}
    </Card>
  );
};

export const NumberCard = ({
  id,
  label,
  loaded,
  error,
  data,
  info,
  className,
}) => {
  const { t } = useTranslation();
  let body;
  if (error) {
    body = <Alert type="error" title={t('PROBLEM_LOADING_DATA')} />;
  } else if (!loaded) {
    body = <LoadingCircle text={t('LOADING')} />;
  } else {
    body = [
      <ValueItem key={0} valueText={data.value.toFixed(2)} />,
      typeof data.delta !== 'undefined' ? (
        <TrendItem key={1} delta={data.delta.toFixed(2)} />
      ) : null,
    ];
  }
  return (
    <Card id={id} label={label} info={info} className={className}>
      {body}
    </Card>
  );
};

export const MetricCard = ({
  id,
  label,
  getMetric,
  type,
  info,
  maxRating,
  className,
  params,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [data, setData] = useState({ value: 0, delta: 0 });
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoaded(false);
        setError(false);
        const payload = await getMetric();
        setData(payload);
        setLoaded(true);
      } catch (e) {
        console.error(e);
        setError(true);
      }
    };
    fetchData();
  }, [params]);

  switch (type) {
    case 'rating':
      return (
        <RatingCard
          id={id}
          label={label}
          info={info}
          loaded={loaded}
          error={error}
          data={data}
          maxRating={maxRating}
          className={className}
        />
      );
    case 'percent':
      return (
        <PercentCard
          id={id}
          label={label}
          info={info}
          loaded={loaded}
          error={error}
          data={data}
          className={className}
        />
      );
    default:
      return (
        <NumberCard
          id={id}
          label={label}
          info={info}
          loaded={loaded}
          error={error}
          data={data}
          className={className}
        />
      );
  }
};
